/**
 * The mechanic-facing job lifecycle.
 *
 * The database tracks a booking with two linked fields: `status` (pending →
 * confirmed → checked_in → in_progress → completed) and a 10-step
 * `journey_stage` that the customer app shows. Staff should not have to think
 * in either, so every screen reads a job through this module instead:
 *
 *   Booked → Checked in → Inspection → In progress → Ready for pickup → Completed
 *
 * plus a single next action, a "waiting on" reason and "needs attention" flags.
 */
import {
  bookingAssignedTechnicianId,
  bookingHandedOver,
  bookingReceptionComplete,
} from './bookingData';

export const JOB_STAGES = ['booked', 'checked_in', 'inspection', 'in_progress', 'ready', 'completed'];

export const STAGE_ICONS = {
  booked: 'event',
  checked_in: 'key',
  inspection: 'troubleshoot',
  in_progress: 'build',
  ready: 'directions_car',
  completed: 'task_alt',
  cancelled: 'block',
};

const JOURNEY_RANK = {
  vehicle_received: 1,
  initial_inspection: 2,
  computer_diagnosis: 3,
  quotation_prepared: 4,
  awaiting_customer_approval: 5,
  spare_parts_requested: 6,
  repair_in_progress: 7,
  quality_inspection: 8,
  car_wash: 9,
  ready_for_collection: 10,
};

export function journeyRank(stage) {
  return JOURNEY_RANK[stage] || 0;
}

/**
 * Bookings finished before the hand-over step existed were already collected.
 * Only jobs completed on or after this date wait in "Ready for pickup".
 */
export const HANDOVER_TRACKING_SINCE = new Date('2026-09-23T00:00:00');

/** A booked car counts as late this long after its slot. */
const LATE_GRACE_MS = 15 * 60 * 1000;

function toDate(value) {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function sameLocalDay(a, b) {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * @param {object} row booking row (BOOKING_SELECT shape)
 * @param {object} [ctx]
 * @param {boolean} [ctx.receptionComplete]  overrides the row's job card flag
 * @param {boolean} [ctx.healthFinalized]    known on the job page only
 * @param {string|null} [ctx.assignedTechnicianId]
 * @param {boolean} [ctx.handedOver]
 * @param {'sent'|'approved'|'rejected'|null} [ctx.quoteStatus]
 * @param {Date} [ctx.now]
 */
export function jobStage(row, ctx = {}) {
  const status = row?.status;
  if (status === 'cancelled') return 'cancelled';

  const rank = journeyRank(row?.journey_stage);
  if (status === 'completed' || rank === 10) {
    const handedOver = ctx.handedOver ?? bookingHandedOver(row);
    if (handedOver) return 'completed';
    const doneAt = toDate(row?.completed_at);
    if (doneAt && doneAt < HANDOVER_TRACKING_SINCE) return 'completed';
    return 'ready';
  }
  if (status === 'in_progress' || rank >= 6) return 'in_progress';

  const received = ctx.receptionComplete ?? bookingReceptionComplete(row);
  if (status === 'checked_in' || received || rank >= 1) {
    return ctx.healthFinalized || rank >= 2 ? 'inspection' : 'checked_in';
  }
  return 'booked';
}

export function stageIndex(stage) {
  return JOB_STAGES.indexOf(stage);
}

/**
 * How long a job may sit in one place before it is flagged, in hours.
 * Keyed by waiting reason first, then stage.
 */
export const OVERDUE_AFTER_HOURS = {
  checked_in: 2,
  inspection: 6,
  in_progress: 24,
  customer: 24,
  parts: 72,
  ready: 48,
};

/** When the job entered the step it is on now. */
export function stageSince(row, stage) {
  const stamps = row?.journey_stage_at || {};
  const current = row?.journey_stage;
  switch (stage) {
    case 'checked_in':
      return toDate(row?.checked_in_at || stamps.vehicle_received);
    case 'inspection':
      return toDate(stamps[current] || stamps.initial_inspection || row?.checked_in_at);
    case 'in_progress':
      return toDate(stamps[current] || row?.in_progress_at);
    case 'ready':
      return toDate(row?.completed_at || stamps.ready_for_collection);
    default:
      return null;
  }
}

/** Attention reasons only the front desk (advisor / manager) can act on. */
const DESK_ONLY = new Set([
  'confirm',
  'late',
  'unassigned',
  'declined',
  'parts_request',
  'qc_request',
  'uncollected',
  'chase_customer',
]);

/**
 * Everything a screen needs to render one job.
 * @param {object} row
 * @param {object} ctx  see jobStage(); plus:
 * @param {string} [ctx.role]  profile role: manager | advisor | technician
 * @param {string} [ctx.userId]
 * @param {{parts:number, qc:number}} [ctx.requests]  pending mechanic requests
 */
export function describeJob(row, ctx = {}) {
  const now = ctx.now || new Date();
  const stage = jobStage(row, ctx);
  const rank = journeyRank(row?.journey_stage);
  const assignedTechnicianId =
    ctx.assignedTechnicianId !== undefined
      ? ctx.assignedTechnicianId
      : bookingAssignedTechnicianId(row);
  const quoteStatus = ctx.quoteStatus || null;
  const scheduledAt = toDate(row?.scheduled_at || row?.created_at);
  const requests = ctx.requests || { parts: 0, qc: 0 };
  const isMechanic = ctx.role === 'technician';
  const working = ['checked_in', 'inspection', 'in_progress'].includes(stage);

  let waiting = null;
  if (working && isMechanic && (requests.parts || requests.qc)) {
    waiting = 'approval';
  } else if (stage === 'inspection' && (quoteStatus === 'sent' || (rank === 5 && !quoteStatus))) {
    waiting = 'customer';
  } else if (stage === 'in_progress' && row?.journey_stage === 'spare_parts_requested') {
    waiting = 'parts';
  }

  const since = stageSince(row, stage);
  const hoursInStage = since ? Math.max(0, (now.getTime() - since.getTime()) / 3600000) : null;
  const limit = OVERDUE_AFTER_HOURS[waiting] ?? OVERDUE_AFTER_HOURS[stage];
  const overdue = hoursInStage != null && limit != null && hoursInStage > limit;

  let attention = [];
  if (row?.status === 'pending') attention.push('confirm');
  if (
    stage === 'booked' &&
    scheduledAt &&
    now.getTime() - scheduledAt.getTime() > LATE_GRACE_MS
  ) {
    attention.push('late');
  }
  if (working && !assignedTechnicianId) attention.push('unassigned');
  if (stage === 'inspection' && quoteStatus === 'rejected') attention.push('declined');
  if (working && requests.parts) attention.push('parts_request');
  if (working && requests.qc) attention.push('qc_request');
  if (overdue) {
    if (stage === 'ready') attention.push('uncollected');
    else if (waiting === 'customer') attention.push('chase_customer');
    else if (waiting !== 'approval') attention.push('stuck');
  }
  if (isMechanic) attention = attention.filter((id) => !DESK_ONLY.has(id));

  return {
    stage,
    rank,
    waiting,
    attention,
    assignedTechnicianId,
    scheduledAt,
    since,
    hoursInStage,
    overdue,
    requests,
    isToday: sameLocalDay(scheduledAt, now),
    completedToday: stage === 'completed' && sameLocalDay(toDate(row?.completed_at), now),
    isMine: !!ctx.userId && assignedTechnicianId === ctx.userId,
    next: nextAction(row, { ...ctx, stage, rank, waiting, quoteStatus, assignedTechnicianId, requests }),
  };
}

/**
 * The one thing to do next.
 * kind:
 *  - 'status'   → updateBookingStatus(target)
 *  - 'journey'  → set_booking_journey_stage(target)
 *  - 'panel'    → open a workspace section (target = checkin|inspection|work|quote|requests)
 *  - 'assign'   → pick a mechanic
 *  - 'handover' → markBookingHandedOver
 *  - 'wait'     → nothing to press; someone else acts
 * `alternatives` are the less common moves from this point.
 */
export function nextAction(row, ctx) {
  const { stage, rank, waiting, quoteStatus, assignedTechnicianId } = ctx;
  const requests = ctx.requests || { parts: 0, qc: 0 };
  const role = ctx.role;
  const frontDesk = role === 'manager' || role === 'advisor';

  // A mechanic asked the desk for something: that comes before the next workflow step.
  if (['checked_in', 'inspection', 'in_progress'].includes(stage) && (requests.qc || requests.parts)) {
    const kind = requests.qc ? 'qc' : 'parts';
    if (frontDesk) {
      return action(`review_${kind}`, 'panel', 'requests', kind === 'qc' ? 'fact_check' : 'inventory_2');
    }
    return action(`await_${kind}_review`, 'wait', null, 'hourglass_top');
  }

  switch (stage) {
    case 'booked':
      if (row?.status === 'pending') {
        return action('confirm', 'status', 'confirmed', 'check_circle');
      }
      if (frontDesk) {
        // Check-in is optional: the desk can mark the car as arrived instead.
        return {
          ...action('checkin', 'panel', 'checkin', 'key'),
          alternatives: [action('skip_checkin', 'skip_checkin', null, 'fast_forward')],
        };
      }
      return action('await_checkin', 'wait', null, 'hourglass_top');

    case 'checked_in':
      if (!assignedTechnicianId && frontDesk) {
        return action('assign', 'assign', null, 'person_add');
      }
      return action('inspect', 'panel', 'inspection', 'troubleshoot');

    case 'inspection': {
      if (ctx.healthFinalized === false) {
        return action('finish_inspection', 'panel', 'inspection', 'troubleshoot');
      }
      const startWork = action('start_work', 'status', 'in_progress', 'play_arrow');
      if (waiting === 'customer') {
        return {
          ...action('await_approval', 'wait', null, 'hourglass_top'),
          alternatives: [startWork],
        };
      }
      if (quoteStatus === 'rejected') {
        return {
          ...action('review_quote', 'panel', 'quote', 'request_quote'),
          alternatives: [startWork],
        };
      }
      if (rank < 4 && quoteStatus !== 'approved') {
        return {
          ...action('prepare_quote', 'panel', 'quote', 'request_quote'),
          alternatives: [startWork],
        };
      }
      return {
        ...startWork,
        alternatives: [action('parts_ordered', 'journey', 'spare_parts_requested', 'inventory_2')],
      };
    }

    case 'in_progress': {
      const markReady = action('mark_ready', 'status', 'completed', 'directions_car');
      const journey = row?.journey_stage;
      if (journey === 'spare_parts_requested') {
        return action('parts_arrived', 'journey', 'repair_in_progress', 'build');
      }
      if (journey === 'quality_inspection') {
        return {
          ...markReady,
          alternatives: [action('car_wash', 'journey', 'car_wash', 'local_car_wash')],
        };
      }
      if (journey === 'car_wash') return markReady;
      return {
        ...action('quality_check', 'journey', 'quality_inspection', 'fact_check'),
        alternatives: [
          markReady,
          action('parts_ordered', 'journey', 'spare_parts_requested', 'inventory_2'),
        ],
      };
    }

    case 'ready':
      return action('handover', 'handover', null, 'key');

    default:
      return null;
  }
}

function action(id, kind, target, icon) {
  return { id, kind, target, icon, alternatives: [] };
}

/**
 * Which Today section a job belongs to. Every job lands in exactly one, so
 * counts add up and nothing is listed twice.
 */
export function todayBucket(job, now = new Date()) {
  const { stage } = job;
  if (stage === 'cancelled') return null;
  if (stage === 'completed') {
    return job.completedToday ? 'done' : null;
  }
  if (job.attention.length) return 'attention';
  if (stage === 'ready') return 'ready';
  if (job.waiting) return 'waiting';
  if (stage === 'booked') {
    if (job.isToday) return 'arriving';
    if (job.scheduledAt && job.scheduledAt > now) return 'upcoming';
    return null;
  }
  return 'workshop';
}

/** Non-exclusive filters for the Jobs list. */
export const JOB_FILTERS = {
  today: (job) =>
    job.isToday || ['checked_in', 'inspection', 'in_progress', 'ready'].includes(job.stage),
  active: (job) => ['checked_in', 'inspection', 'in_progress'].includes(job.stage),
  upcoming: (job, now) =>
    job.stage === 'booked' && !!job.scheduledAt && job.scheduledAt > now && !job.isToday,
  waiting: (job) => !!job.waiting,
  attention: (job) => job.attention.length > 0,
  ready: (job) => job.stage === 'ready',
  completed: (job) => job.stage === 'completed',
  cancelled: (job) => job.stage === 'cancelled',
  all: () => true,
};

export const JOB_FILTER_IDS = Object.keys(JOB_FILTERS);

/** Next steps safe to run straight from a list (no form, no gate to check). */
// Hand-over is not here: it asks whether the customer paid, on the job page.
export const QUICK_ACTIONS = new Set(['confirm', 'quality_check', 'parts_arrived', 'mark_ready']);
